import React from 'react';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import _ from 'lodash';
import { formatNumbersToCurrencyString, openLinkInBlank } from 'utils/utils';
import { calculateDaysPastDue, convertDateFromApiToUserTimezone } from 'utils/utility_functions/timezone';
import constants from 'constants/index';
import GlobalTooltip from 'components/GlobalTooltip';
import { setLocalStorageItem } from 'utils/localStorageUtils';

/**
 * Case column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @returns {JSX.Element} - Case cell
 */
export const caseBody = (params) => {
  const { rowData } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Case</span>
        <span>-</span>
      </React.Fragment>
    );
  }
  const { is_lead } = rowData;
  const mainModuleName = is_lead ? '/leads' : '/cases';
  let route = rowData?.invoice?.case_id ? `${mainModuleName}/${rowData?.invoice?.case_id}` : mainModuleName;

  return (
    <React.Fragment>
      <span className="p-column-title text-break">Case</span>
      <Link
        to={route}
        className="redirection-link-no-text-transform"
        title={rowData?.invoice?.case_name ? rowData.invoice?.case_name : undefined}
      >
        {rowData?.invoice?.case_name ? rowData.invoice?.case_name : '-'}
      </Link>
    </React.Fragment>
  );
};

/**
 * Viewed column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @param {string} params.timezone - User timezone
 * @returns {JSX.Element} - Viewed cell
 */
export const viewBody = (params) => {
  const { rowData, timezone } = params || {};
  if (!rowData) {
    return (
      <>
        <span className="p-column-title text-break">Viewed</span>
        <span>-</span>
      </>
    );
  }
  const formattedDate = Date.parse(rowData?.viewed_status)
    ? convertDateFromApiToUserTimezone(rowData?.viewed_status, timezone, constants.month_date_year_format)
    : rowData?.viewed_status;
  return (
    <>
      <span className="p-column-title text-break">Viewed</span>
      <span title={formattedDate}>{formattedDate}</span>
    </>
  );
};

/**
 * Total Fee column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @returns {JSX.Element} - Total Fee cell
 */
export const totalFeeBody = (params) => {
  const { rowData } = params || {};
  if (rowData?.total_amount == null) {
    return null;
  }
  return (
    <>
      <span className="p-column-title text-break">Total Fee</span>
      <span className="text-break" title={formatNumbersToCurrencyString(rowData?.total_amount)}>
        {formatNumbersToCurrencyString(rowData?.total_amount)}
      </span>
    </>
  );
};

/**
 * Status column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @returns {JSX.Element} - Status cell
 */
export const statusBody = (params) => {
  const { rowData } = params || {};
  if (!rowData?.invoice_status) {
    return null;
  }
  return (
    <>
      <span className="p-column-title text-break">Status</span>
      <span title={_.startCase(_.toLower(rowData?.invoice_status))}>{_.startCase(_.toLower(rowData?.invoice_status))}</span>
    </>
  );
};

/**
 * Due column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @returns {JSX.Element} - Due cell
 */
export const dueBody = (params) => {
  const { rowData } = params || {};
  if (!rowData) {
    return (
      <>
        <span className="p-column-title text-break">Due</span>
        <span>-</span>
      </>
    );
  }
  return (
    <>
      <span className="p-column-title text-break">Due</span>
      {rowData?.is_payment_plan &&
        (rowData?.plan_due_amount != null || rowData?.due_amount != null) &&
        rowData?.invoice_status !== 'FORWARDED' && (
          <span className="text-break" title={formatNumbersToCurrencyString(rowData?.due_amount)}>
            {formatNumbersToCurrencyString(rowData?.due_amount)}
          </span>
        )}
      {(!rowData?.is_payment_plan || rowData?.invoice_status === 'FORWARDED') && rowData?.due_amount != null && (
        <span className="text-break" title={formatNumbersToCurrencyString(rowData?.due_amount)}>
          {formatNumbersToCurrencyString(rowData?.due_amount)}
        </span>
      )}
      {rowData?.invoice_unpaid_ref_number && (
        <span className="text-break ms-1" title={`Forwarded to ${rowData?.invoice_unpaid_ref_number}`}>
          Forwarded to {rowData?.invoice_unpaid_ref_number}
        </span>
      )}
    </>
  );
};

/**
 * Due Today column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @returns {JSX.Element} - Due Today cell
 */
export const dueTodayBody = (params) => {
  const { rowData } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Due Today</span>
        <span className="text-break">-</span>
      </React.Fragment>
    );
  }

  const displayAmount = rowData?.due_today ? formatNumbersToCurrencyString(rowData?.due_today) : formatNumbersToCurrencyString(0.0);
  const titleText = rowData?.due_today ? formatNumbersToCurrencyString(rowData?.due_today) : formatNumbersToCurrencyString(0.0);

  return (
    <React.Fragment>
      <span className="p-column-title text-break">Due Today</span>
      <span className="text-break" title={titleText}>
        {displayAmount}
      </span>
    </React.Fragment>
  );
};

/**
 * Next Due Date column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @param {string} params.timezone - User timezone
 * @returns {JSX.Element} - Next Due Date cell
 */
export const nextDueDateBody = (params) => {
  const { rowData, timezone } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Next Due Date</span>
        <span className="text-break">-</span>
      </React.Fragment>
    );
  }
  const formattedDate = convertDateFromApiToUserTimezone(rowData?.due_date, timezone, constants.month_date_year_format);
  return (
    <React.Fragment>
      <span className="p-column-title text-break">Next Due Date</span>
      <span className="text-break" title={rowData?.invoice_status === 'PAID' ? undefined : formattedDate}>
        {rowData?.invoice_status === 'PAID' ? '-' : formattedDate}
      </span>
    </React.Fragment>
  );
};

/**
 * Sent Date column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @param {string} params.timezone - User timezone
 * @returns {JSX.Element} - Sent Date cell
 */
export const dueDateBody = (params) => {
  const { rowData, timezone } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Sent Date</span>
        <span className="text-break">-</span>
      </React.Fragment>
    );
  }
  const formattedDate = convertDateFromApiToUserTimezone(rowData?.invoice_date, timezone, constants.month_date_year_format);
  return (
    <React.Fragment>
      <span className="p-column-title text-break">Sent Date</span>
      <span className="text-break" title={formattedDate}>
        {formattedDate}
      </span>
    </React.Fragment>
  );
};

/**
 * Days Past Due column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @param {string} params.timezone - User timezone
 * @returns {JSX.Element} - Days Past Due cell
 */
export const daysPastDueBody = (params) => {
  const { rowData, timezone } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Days Past Due</span>
        <span className="text-break">-</span>
      </React.Fragment>
    );
  }
  const dueDays =
    rowData?.invoice_status === 'PAID' || !rowData?.days_past_due
      ? '-'
      : rowData?.days_past_due > 0
      ? `${rowData.days_past_due} day${rowData.days_past_due > 1 ? 's' : ''}`
      : '-';
  return (
    <React.Fragment>
      <span className="p-column-title text-break">Days Past Due</span>
      <span className="text-break" title={dueDays ? dueDays : undefined}>
        {dueDays}
      </span>
    </React.Fragment>
  );
};

/**
 * Action column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @param {Function} params.onView - View handler function
 * @param {Function} params.onSendMail - Send mail handler function
 * @param {Function} params.onRecordPayment - Record payment handler function
 * @param {Function} params.onAcceptPayment - Accept payment handler function
 * @param {Function} params.onConfidoPay - Confido pay handler function
 * @param {boolean} params.isConfidoLinkLoading - Whether Confido payment link is loading
 * @param {Function} params.onReminder - Reminder handler function
 * @param {Function} params.onDelete - Delete handler function
 * @param {Function} params.onDownload - Portal download (send-invoice API with is_portal_download)
 * @param {Object} params.userContext - User context
 * @returns {JSX.Element} - Action cell
 */
export const actionBody = (params) => {
  const {
    rowData,
    onView,
    onSendMail,
    onRecordPayment,
    onAcceptPayment,
    onConfidoPay,
    isConfidoLinkLoading,
    onReminder,
    onDelete,
    onDownload,
    userContext,
  } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Action</span>
        <span>-</span>
      </React.Fragment>
    );
  }

  let due_amount = rowData?.due_amount;
  due_amount = due_amount ? parseInt(due_amount) : 0;

  const onClickViewInvoiceDetailsLink = (e) => {
    e.preventDefault();

    const isLead = rowData?.invoice?.is_lead;
    const caseId = rowData?.invoice?.case_id;

    const mainModuleName = isLead ? '/leads' : '/cases';
    const route = caseId ? `${mainModuleName}/${caseId}/billing` : mainModuleName;
    const stateToPass = { invoiceDetails: rowData };

    setLocalStorageItem('viewInvoice', true);

    if (!openLinkInBlank(route, stateToPass)) {
      onView?.(rowData);
    }
  };

  return (
    <React.Fragment>
      <span className="p-column-title text-break">Action</span>
      <span role="button" onClick={(e) => onClickViewInvoiceDetailsLink(e)} className="text-primary-dark">
        View
      </span>
      {due_amount > 0 && (
        <span role="button" onClick={(e) => onSendMail?.(rowData)}>
          <GlobalTooltip tooltip="Payment Request" className={'ms-2'}>
            <i className="icon-send-form" />
          </GlobalTooltip>
        </span>
      )}
      {due_amount > 0 && rowData?.invoice_status !== 'FORWARDED' && rowData?.invoice_status !== 'PAID' && (
        <span role="button">
          <GlobalTooltip tooltip="Record Payment" className={'ms-2'}>
            <i className="icon-radio" onClick={(e) => onRecordPayment?.(rowData)} />
          </GlobalTooltip>
        </span>
      )}
      {due_amount > 0 && userContext?.userDetails?.is_quickbook_connected && rowData?.invoice_status !== 'FORWARDED' && (
        <span role="button">
          <GlobalTooltip tooltip="Pay" className={'ms-2'}>
            <i className="bi bi-credit-card-2-back" style={{ color: '#7f8f9f' }} onClick={() => onAcceptPayment?.(rowData)} />
          </GlobalTooltip>
        </span>
      )}
      {due_amount > 0 && userContext?.userDetails?.is_confido_connected && rowData?.invoice_status !== 'FORWARDED' && (
        <span role="button">
          <GlobalTooltip
            tooltip={rowData?.confido_bank_account_id ? 'Pay' : 'No Confido Bank Account mapped with this invoice deposit account'}
            className={'ms-2'}
          >
            <i
              className={`bi bi-credit-card-2-back${isConfidoLinkLoading || !rowData?.confido_bank_account_id ? ' p-disabled' : ''}`}
              style={{ color: '#7f8f9f' }}
              onClick={() => onConfidoPay?.(rowData)}
            />
          </GlobalTooltip>
        </span>
      )}
      <span role="button" onClick={(e) => onReminder?.(rowData)}>
        <GlobalTooltip tooltip="Reminder" className="ms-2">
          <i className="icon-notification" />
        </GlobalTooltip>
      </span>
      <span
        role="button"
        aria-label="Download"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDownload?.(rowData);
        }}
      >
        <GlobalTooltip tooltip="Download" className="ms-2">
          <i className="icon-save cursor-pointer" />
        </GlobalTooltip>
      </span>
      {rowData?.invoice_status !== 'PARTIAL' &&
        rowData?.invoice_status !== 'PAID' &&
        rowData?.invoice_status !== 'CURRENT' &&
        rowData?.invoice_status !== 'FORWARDED' &&
        rowData?.invoice_status !== 'carried_forward_balance_due' && (
          <span role="button">
            <GlobalTooltip tooltip="Delete" className="ms-2">
              <i className="icon-delete cursor-pointer" onClick={() => onDelete?.(rowData)} />
            </GlobalTooltip>
          </span>
        )}
    </React.Fragment>
  );
};
